
import React from "react";
import { Button } from "@rivtn/noseur";
import { i18nManager } from "../../i18n";
import Simulate from "../simulate/Simulate";
import { FormStep, Schemes, Utils } from "../../utils";
import { Svgs } from "../../constants/Images";
import { Status } from "../../constants/Status";
import loader from "../../assets/images/loading_circle.gif";
import { PaymentMethod } from "../../constants/PaymentMethod";
import { ITransaction, ITransactionCharge, PloutousService, SanitizedResponse } from "../../services";

export interface BankAccountDetail {
    name: string;
    number: string;
    bankName: string;
    processor?: string;
    __chargeData?: string;
}

export interface BankTransferProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function BankTransfer(props: BankTransferProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const ploutousService = PloutousService.getInstance();
    const labels = i18nManager.Labels.method.bank_transfer;
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [serviceErrors, setServiceErrors] = React.useState<string[]>([]);
    const [bankDetail, setBankDetail] = React.useState<BankAccountDetail>();
    const [serviceSuccesses, setServiceSuccesses] = React.useState<string[]>([]);
    const [serviceWarnings, setServiceWarnings] = React.useState<string[]>([]);
    const [confirmingPayment, setConfirmingPayment] = React.useState<boolean>(false);
    const [step, setStep] = React.useState<"LOADING_BANK_DETAILS" | "CONFIRM_PAYMENT">("LOADING_BANK_DETAILS");
    const countryAndCurrency = i18nManager.CountriesAndCurrencies.find(l => l.value === transaction.charge_country_code);

    React.useEffect(fetchBankDetails, []);

    if (serviceSuccesses.length) {
        return Utils.buildPaymentCompletedView(transaction, serviceSuccesses);
    }

    if (serviceWarnings.length) {
        return Utils.buildPartialPaymentView(transaction, serviceWarnings, () => {
            setServiceWarnings([]);
            setStep("LOADING_BANK_DETAILS");
            fetchBankDetails();
        }, () => {
            setServiceWarnings([]);
            setStep(FormStep.CONFIRM_PAYMENT);
        });
    }

    if (serviceErrors.length) {
        return Utils.buildPaymentErrorView(transaction, serviceErrors, () => {
            setStep("LOADING_BANK_DETAILS");
            fetchBankDetails();
            setServiceErrors([]);
        });
    }

    return (<div className="bank-transfer">
        <span className="title">{labels.title.replace("${amount}", `${countryAndCurrency?.currency_sign} ${(transaction.amount/ Utils.AMOUNT_UNIT)}`)}</span>
        <div className="bank-details">
            {step === "LOADING_BANK_DETAILS" && (<img src={loader} className="loading-img" alt={labels.fetching_details} />)}
            {step === "CONFIRM_PAYMENT" && (<>
                <div className="info">
                    <span className="name">{labels.bank_name}</span>
                    <span className="value">{bankDetail?.bankName}</span>
                </div>
                <div className="info no">
                    <span className="name">{labels.account_number}</span>
                    <span className="value">{bankDetail?.number} {Svgs.Copy(copyBankAccountNumber)}</span>
                </div>
                <div className="info">
                    <span className="name">{labels.account_name}</span>
                    <span className="value">{bankDetail?.name}</span>
                </div>
            </>)}
        </div>
        {(step === "CONFIRM_PAYMENT" && bankDetail?.processor === "SIM") && (<Simulate currency={countryAndCurrency?.currency_sign} pages={{
            one: {
                inputType: "AMOUNT",
                defaultInput: `${(transaction.amount/ Utils.AMOUNT_UNIT)}`,
                action: async (simulate: "SUCCESS" | "FAILURE", amount?: string) => {
                    try {
                        const payload = {
                            simulate,
                            amount: parseFloat(amount ?? "-1"),
                        };
                        await ploutousService.simulateChargePayment(bankDetail?.__chargeData ?? "", payload);
                        Utils.toastSuccess(i18nManager.Labels.simulator.successful);
                    } catch (error: any) {
                        PloutousService.reportError(error);
                    }
                }
            }
        }} />)}
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        <Button raised fill scheme={Schemes.RIVTN_PLOUTOUS} className="pay" text={i18nManager.Labels.common.confirm_payment}
            {...(confirmingPayment ? Utils.BUTTON_LOADING_PROPS : {})} disabled={(step === "LOADING_BANK_DETAILS") || confirmingPayment} onClick={() => confirmPayment()} />
    </div>);

    function fetchBankDetails() {
        ploutousService.initiateCharge(transaction, props.businessId, PaymentMethod.BANK_TRANSFER).then(({ sanitized }: SanitizedResponse<ITransactionCharge>) => {
            setBankDetail({
                bankName: sanitized.result.bank_name,
                name: sanitized.result.bank_account_name,
                processor: sanitized.extra_data?.processor,
                number: sanitized.result.bank_account_number,
                __chargeData: sanitized.extra_data?.charge_data
            });
            setStep(FormStep.CONFIRM_PAYMENT);
        }).catch((error: any) => {
            setServiceErrors(error.errors ?? [error.message]);
        });
    }

    async function confirmPayment(showModal: boolean = true) {
        if (showModal) setLoading(true);
        props.fetchTransaction((result?: ITransaction, error?: any) => {
            setLoading(false);
            if (error) {
                setServiceErrors(error.errors ?? [error.message]);
                return;
            }
            if ((result?.status ?? 0) === Status.TRANSACTION_PARTIALLY_PAID) {
                setServiceWarnings([i18nManager.Labels.common.partially_paid]);
                return;
            } else if ((result?.status ?? 0) > Status.TRANSACTION_FULLY_PAID_PENDING_ACKNOWLEDGEMENT) {
                props.paymentSuccessful();
                setServiceSuccesses([i18nManager.Labels.common.thank_you, i18nManager.Labels.common.payment_successful]);
                return;
            }
            if (!confirmingPayment) setConfirmingPayment(true);
            setTimeout(() => confirmPayment(false), 2000);
            console.log("Initiate web socket connection to track payment completion");
            // TODO in the API service implements logic to fetch every 2 seconds 5 times from processor once a verify transaction is received
            // also for double webhook log it to be able to detect duplicate payments from processor
        });
    }

    async function copyBankAccountNumber() {
        if (!bankDetail?.number) return;
        Utils.copyToClipboard(bankDetail?.number, (err) => {
            if (!err) {
                Utils.toastSuccess(labels.account_number_copied);
            } else {
                Utils.toastError(`${err}`);
            }
        });
    }

}

export default BankTransfer;
